import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    /* ── Host ────────────────────────────────────────────────────────────────── */
    :host {
      display: block;
      position: relative;

      /* Bar-level cascade tokens */
      --_bar-bg:    var(--uwc-megamenu-bg,    ${t.surface});
      --_bar-color: var(--uwc-megamenu-color, ${t.text});
    }

    /* ─────────────────────────────────────────────────────────────────────────
       HORIZONTAL BAR
    ───────────────────────────────────────────────────────────────────────── */

    .uwc-mm {
      display: flex;
      align-items: center;
      gap: var(--uwc-megamenu-gap, ${t.space1});
      min-height: var(--uwc-megamenu-height, 3rem);
      padding: var(--uwc-megamenu-padding, ${t.space2} ${t.space4});
      background: var(--_bar-bg);
      color: var(--_bar-color);
      border: var(--uwc-megamenu-border, 1px solid ${t.border});
      border-radius: var(--uwc-megamenu-radius, ${t.radiusMd});
      box-shadow: var(--uwc-megamenu-shadow, none);
      position: relative;
    }

    /* ── Slots ───────────────────────────────────────────────────────────────── */
    .uwc-mm__start {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin-inline-end: ${t.space2};
    }
    .uwc-mm__start:empty { display: none; }

    .uwc-mm__end {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin-inline-start: auto;
    }
    .uwc-mm__end:empty { display: none; }

    /* ── Desktop nav list ────────────────────────────────────────────────────── */
    .uwc-mm__list {
      display: flex;
      align-items: center;
      list-style: none;
      margin: 0;
      padding: 0;
      gap: var(--uwc-megamenu-gap, ${t.space1});
      flex: 1;
    }

    /* ── Top-level item ──────────────────────────────────────────────────────── */
    .uwc-mm__item {
      position: relative;
    }

    /* ── Top-level button / link ─────────────────────────────────────────────── */
    .uwc-mm__item-btn {
      display: inline-flex;
      align-items: center;
      gap: ${t.space2};
      padding: var(--uwc-megamenu-item-padding, ${t.space2} ${t.space3});
      font-size: var(--uwc-megamenu-item-font-size, ${t.fontSizeMd});
      font-weight: var(--uwc-megamenu-item-font-weight, ${t.fontWeightMedium});
      font-family: var(--uwc-font-family, inherit);
      color: inherit;
      background: transparent;
      border: none;
      border-radius: var(--uwc-megamenu-item-radius, ${t.radiusMd});
      cursor: pointer;
      white-space: nowrap;
      text-align: left;
      text-decoration: none;
      line-height: 1.4;
      transition:
        background ${t.durationFast} ${t.easingStandard},
        color       ${t.durationFast} ${t.easingStandard};
    }
    .uwc-mm__item-btn:hover {
      background: var(--uwc-megamenu-item-hover-bg,
        color-mix(in oklab, var(--_bar-color) 8%, transparent));
    }
    .uwc-mm__item-btn:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 2px;
    }
    .uwc-mm__item-btn[aria-expanded="true"],
    .uwc-mm__item--active .uwc-mm__item-btn {
      background: var(--uwc-megamenu-item-active-bg, ${t.selectedBg});
    }
    .uwc-mm__item--selected > .uwc-mm__item-btn {
      background: var(--uwc-megamenu-item-active-bg, ${t.selectedBg});
      color: var(--uwc-color-primary, ${t.primary});
    }
    .uwc-mm__item-btn[disabled] {
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none;
    }

    .uwc-mm__item-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      flex-shrink: 0;
    }
    .uwc-mm__item-label { flex: 1; }
    .uwc-mm__item-badge {
      font-size: ${t.fontSizeXs};
      padding: 1px 6px;
      border-radius: ${t.radiusFull};
      background: var(--uwc-megamenu-badge-bg, var(--uwc-color-primary, ${t.primary}));
      color: var(--uwc-megamenu-badge-color, #fff);
      font-weight: ${t.fontWeightMedium};
      flex-shrink: 0;
    }

    /* Chevron — rotates when panel is open */
    .uwc-mm__chevron {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      flex-shrink: 0;
      color: color-mix(in oklab, currentColor 55%, transparent);
      transition: transform ${t.durationBase} ${t.easingStandard};
    }

    /* Vertical separator between top-level items */
    .uwc-mm__h-sep {
      width: 1px;
      height: 1.25rem;
      background: ${t.border};
      margin: 0 ${t.space2};
    }

    /* ─────────────────────────────────────────────────────────────────────────
       MEGA PANEL  (popover — lives in the top layer; position set via JS)
       KEY DESIGN: --_panel-color and --_panel-bg are set here and cascade
       to EVERY child element via color: inherit / color-mix().
    ───────────────────────────────────────────────────────────────────────── */
    .uwc-mm__panel {
      /* JS sets left / top / width / maxHeight via inline styles */
      position: fixed;
      inset: 0 auto auto 0;
      margin: 0;

      /* ── Cascade tokens — every child inherits these ── */
      --_panel-bg:    var(--uwc-megamenu-panel-bg,    var(--uwc-surface, ${t.surface}));
      --_panel-color: var(--uwc-megamenu-panel-color, var(--uwc-text,    ${t.text}));

      background: var(--_panel-bg);
      color:      var(--_panel-color);
      border: var(--uwc-megamenu-panel-border, 1px solid ${t.border});
      border-radius: var(--uwc-megamenu-panel-radius, ${t.radiusLg});
      box-shadow: var(--uwc-megamenu-panel-shadow, ${t.shadowLg});
      padding: var(--uwc-megamenu-panel-padding, ${t.space4});
      overflow-y: auto;
      z-index: var(--uwc-megamenu-z, ${t.zFloat});

      /* Entry / exit animation driven by :popover-open */
      opacity: 0;
      transform: scaleY(0.96) translateY(-6px);
      transform-origin: top center;
      transition:
        opacity   var(--uwc-megamenu-duration, ${t.durationslow}) ${t.easingStandard},
        transform var(--uwc-megamenu-duration, ${t.durationslow}) ${t.easingStandard},
        display   var(--uwc-megamenu-duration, ${t.durationslow}) allow-discrete,
        overlay   var(--uwc-megamenu-duration, ${t.durationslow}) allow-discrete;
    }
    .uwc-mm__panel:popover-open {
      opacity: 1;
      transform: scaleY(1) translateY(0);
    }
    @starting-style {
      .uwc-mm__panel:popover-open {
        opacity: 0;
        transform: scaleY(0.96) translateY(-6px);
      }
    }

    /* ── Column grid ─────────────────────────────────────────────────────────── */
    .uwc-mm__columns {
      display: flex;
      gap: var(--uwc-megamenu-col-gap, ${t.space4});
      align-items: flex-start;
    }
    .uwc-mm__columns--v {
      flex-direction: column;
      gap: ${t.space3};
    }

    /* ── Single column ───────────────────────────────────────────────────────── */
    .uwc-mm__col {
      flex: 1;
      min-width: 0;
    }

    /* ── Column header (section label) ──────────────────────────────────────── */
    .uwc-mm__col-header {
      margin: 0 0 ${t.space2} 0;
      padding: 0 ${t.space3} ${t.space2};
      /* Derived from panel color: 55% opacity gives a muted label look */
      color: var(--uwc-megamenu-col-header-color,
        color-mix(in oklab, var(--_panel-color) 55%, transparent));
      font-size: var(--uwc-megamenu-col-header-font-size, ${t.fontSizeSm});
      font-weight: var(--uwc-megamenu-col-header-font-weight, ${t.fontWeightSemibold});
      letter-spacing: 0.05em;
      text-transform: uppercase;
      border-bottom: 1px solid var(--uwc-megamenu-separator-color,
        color-mix(in oklab, var(--_panel-color) 12%, transparent));
    }

    /* ── Column item list ────────────────────────────────────────────────────── */
    .uwc-mm__col-list {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    /* ── Column item ─────────────────────────────────────────────────────────── */
    .uwc-mm__col-item {
      display: flex;
      align-items: center;
      gap: ${t.space2};
      width: 100%;
      padding: var(--uwc-megamenu-col-item-padding, ${t.space2} ${t.space3});
      font-size: var(--uwc-megamenu-col-item-font-size, ${t.fontSizeMd});
      font-weight: ${t.fontWeightMedium};
      font-family: var(--uwc-font-family, inherit);
      /* All text uses panel color — the cascade magic */
      color: inherit;
      background: transparent;
      border: none;
      border-radius: var(--uwc-megamenu-col-item-radius, ${t.radiusMd});
      cursor: pointer;
      white-space: nowrap;
      text-align: left;
      text-decoration: none;
      line-height: 1.4;
      transition:
        background ${t.durationFast} ${t.easingStandard},
        color       ${t.durationFast} ${t.easingStandard};
    }
    .uwc-mm__col-item:hover,
    .uwc-mm__col-item:focus-visible {
      /* Hover = 10% of panel text color on transparent — adapts to any bg */
      background: var(--uwc-megamenu-col-item-hover-bg,
        color-mix(in oklab, var(--_panel-color) 10%, transparent));
      outline: none;
    }
    .uwc-mm__col-item:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 1px;
    }
    .uwc-mm__col-item--disabled,
    .uwc-mm__col-item[disabled] {
      opacity: 0.42;
      cursor: not-allowed;
      pointer-events: none;
    }
    /* Danger items use the danger color but still inherit everything else */
    .uwc-mm__col-item--danger {
      color: var(--uwc-megamenu-col-item-danger-color, var(--uwc-color-danger, ${t.danger}));
    }
    .uwc-mm__col-item--danger:hover {
      background: color-mix(in oklab,
        var(--uwc-megamenu-col-item-danger-color, var(--uwc-color-danger, ${t.danger})) 10%,
        transparent);
    }
    .uwc-mm__col-item--selected {
      background: var(--uwc-megamenu-col-item-active-bg,
        color-mix(in oklab, var(--_panel-color) 12%, transparent));
    }

    /* ── Column item sub-elements (all use inherit) ──────────────────────────── */
    .uwc-mm__col-item-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      flex-shrink: 0;
      color: inherit; /* icon follows panel color */
    }
    .uwc-mm__col-item-label { flex: 1; }
    .uwc-mm__col-item-badge {
      font-size: ${t.fontSizeXs};
      padding: 1px 6px;
      border-radius: ${t.radiusFull};
      background: var(--uwc-megamenu-badge-bg, var(--uwc-color-primary, ${t.primary}));
      color: var(--uwc-megamenu-badge-color, #fff);
      font-weight: ${t.fontWeightMedium};
      flex-shrink: 0;
    }

    /* ── Separator ───────────────────────────────────────────────────────────── */
    /* Adapts automatically because it uses panel color at low opacity */
    .uwc-mm__separator {
      height: 1px;
      background: var(--uwc-megamenu-separator-color,
        color-mix(in oklab, var(--_panel-color, ${t.text}) 12%, transparent));
      margin: ${t.space1} ${t.space3};
    }
    /* When separator is a <li role="separator"> */
    li.uwc-mm__separator {
      display: block;
    }

    /* ─────────────────────────────────────────────────────────────────────────
       HAMBURGER  (mobile)
    ───────────────────────────────────────────────────────────────────────── */
    .uwc-mm__hamburger {
      display: none;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-inline-start: auto;
      background: transparent;
      border: none;
      border-radius: var(--uwc-megamenu-item-radius, ${t.radiusMd});
      cursor: pointer;
      color: inherit;
      flex-shrink: 0;
      transition: background ${t.durationFast} ${t.easingStandard};
    }
    .uwc-mm__hamburger:hover {
      background: color-mix(in oklab, var(--_bar-color) 8%, transparent);
    }
    .uwc-mm__hamburger:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 2px;
    }

    /* ─────────────────────────────────────────────────────────────────────────
       MOBILE PANEL  (popover, full-bar-width accordion)
       Uses the same --_panel-color / --_panel-bg cascade as the desktop panel.
    ───────────────────────────────────────────────────────────────────────── */
    .uwc-mm__mobile-panel {
      position: fixed;
      inset: 0 auto auto 0;
      margin: 0;
      list-style: none;
      padding: ${t.space2} 0;

      --_panel-bg:    var(--uwc-megamenu-panel-bg,    var(--uwc-surface, ${t.surface}));
      --_panel-color: var(--uwc-megamenu-panel-color, var(--uwc-text,    ${t.text}));

      background: var(--_panel-bg);
      color: var(--_panel-color);
      border: var(--uwc-megamenu-panel-border, 1px solid ${t.border});
      border-radius: var(--uwc-megamenu-panel-radius, ${t.radiusLg});
      box-shadow: var(--uwc-megamenu-panel-shadow, ${t.shadowLg});
      overflow-y: auto;
      z-index: var(--uwc-megamenu-z, ${t.zFloat});

      opacity: 0;
      transform: scaleY(0.96) translateY(-4px);
      transform-origin: top center;
      transition:
        opacity   var(--uwc-megamenu-duration, ${t.durationBase}) ${t.easingStandard},
        transform var(--uwc-megamenu-duration, ${t.durationBase}) ${t.easingStandard},
        display   var(--uwc-megamenu-duration, ${t.durationBase}) allow-discrete,
        overlay   var(--uwc-megamenu-duration, ${t.durationBase}) allow-discrete;
    }
    .uwc-mm__mobile-panel:popover-open {
      opacity: 1;
      transform: scaleY(1) translateY(0);
    }
    @starting-style {
      .uwc-mm__mobile-panel:popover-open {
        opacity: 0;
        transform: scaleY(0.96) translateY(-4px);
      }
    }

    /* ── Mobile top-level item ───────────────────────────────────────────────── */
    .uwc-mm__mob-btn {
      display: flex;
      align-items: center;
      gap: ${t.space2};
      width: 100%;
      padding: ${t.space3} ${t.space4};
      font-size: ${t.fontSizeMd};
      font-weight: ${t.fontWeightMedium};
      font-family: var(--uwc-font-family, inherit);
      color: inherit;
      background: transparent;
      border: none;
      cursor: pointer;
      text-align: left;
    }
    .uwc-mm__mob-btn:hover {
      background: color-mix(in oklab, var(--_panel-color) 8%, transparent);
    }
    .uwc-mm__mob-btn:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }
    .uwc-mm__mob-btn[disabled] {
      opacity: 0.42;
      cursor: not-allowed;
    }

    .uwc-mm__mob-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      flex-shrink: 0;
    }
    .uwc-mm__mob-label { flex: 1; }
    .uwc-mm__mob-badge {
      font-size: ${t.fontSizeXs};
      padding: 1px 6px;
      border-radius: ${t.radiusFull};
      background: var(--uwc-megamenu-badge-bg, var(--uwc-color-primary, ${t.primary}));
      color: var(--uwc-megamenu-badge-color, #fff);
      font-weight: ${t.fontWeightMedium};
    }

    /* ── Mobile sub-panel (accordion content) ────────────────────────────────── */
    .uwc-mm__mob-sub {
      padding: 0 0 ${t.space2};
      background: color-mix(in oklab, var(--_panel-color) 4%, transparent);
    }
    .uwc-mm__mob-col-header {
      margin: 0;
      padding: ${t.space2} ${t.space6};
      font-size: ${t.fontSizeXs};
      font-weight: ${t.fontWeightSemibold};
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: color-mix(in oklab, var(--_panel-color) 50%, transparent);
    }
    .uwc-mm__mob-col-list {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .uwc-mm__mob-col-item {
      display: flex;
      align-items: center;
      gap: ${t.space2};
      width: 100%;
      padding: ${t.space2} ${t.space6};
      font-size: ${t.fontSizeMd};
      font-family: var(--uwc-font-family, inherit);
      color: inherit;
      background: transparent;
      border: none;
      cursor: pointer;
      text-align: left;
      text-decoration: none;
    }
    .uwc-mm__mob-col-item:hover {
      background: color-mix(in oklab, var(--_panel-color) 8%, transparent);
    }
    .uwc-mm__mob-col-item[disabled] {
      opacity: 0.42;
      cursor: not-allowed;
    }

    /* Mobile col-li separator */
    .uwc-mm__mob-col-list .uwc-mm__separator {
      margin: ${t.space1} ${t.space6};
    }

    /* ─────────────────────────────────────────────────────────────────────────
       VERTICAL SIDEBAR
    ───────────────────────────────────────────────────────────────────────── */
    :host([orientation="vertical"]) .uwc-mm {
      flex-direction: column;
      align-items: stretch;
      padding: var(--uwc-megamenu-padding, ${t.space2});
      gap: 0;
      height: 100%;
      width: var(--uwc-megamenu-vertical-width, 100%);
    }

    .uwc-mm--vertical .uwc-mm__v-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: ${t.space1};
    }

    .uwc-mm__v-item {
      display: flex;
      flex-direction: column;
    }

    .uwc-mm__v-item-btn {
      display: flex;
      align-items: center;
      gap: ${t.space2};
      width: 100%;
      padding: var(--uwc-megamenu-item-padding, ${t.space2} ${t.space3});
      font-size: var(--uwc-megamenu-item-font-size, ${t.fontSizeMd});
      font-weight: var(--uwc-megamenu-item-font-weight, ${t.fontWeightMedium});
      font-family: var(--uwc-font-family, inherit);
      color: inherit;
      background: transparent;
      border: none;
      border-radius: var(--uwc-megamenu-item-radius, ${t.radiusMd});
      cursor: pointer;
      text-align: left;
      line-height: 1.4;
      transition: background ${t.durationFast} ${t.easingStandard};
    }
    .uwc-mm__v-item-btn:hover {
      background: var(--uwc-megamenu-item-hover-bg,
        color-mix(in oklab, var(--_bar-color) 8%, transparent));
    }
    .uwc-mm__v-item-btn:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 2px;
    }
    .uwc-mm__v-item-btn[disabled] {
      opacity: 0.42;
      cursor: not-allowed;
    }
    .uwc-mm__v-item--active > .uwc-mm__v-item-btn {
      background: var(--uwc-megamenu-item-active-bg, ${t.selectedBg});
    }
    .uwc-mm__v-item--selected > .uwc-mm__v-item-btn {
      background: var(--uwc-megamenu-item-active-bg, ${t.selectedBg});
      color: var(--uwc-color-primary, ${t.primary});
    }

    /* ── Vertical inline panel ───────────────────────────────────────────────── */
    .uwc-mm__v-panel {
      /* Same cascade tokens as horizontal panel */
      --_panel-bg:    var(--uwc-megamenu-panel-bg,    transparent);
      --_panel-color: var(--uwc-megamenu-panel-color, var(--uwc-text, ${t.text}));

      background: var(--_panel-bg);
      color: var(--_panel-color);
      border-radius: var(--uwc-megamenu-col-item-radius, ${t.radiusMd});
      padding: ${t.space2} 0 ${t.space2} ${t.space4};
    }

    /* Vertical panel reuses .uwc-mm__col-header, .uwc-mm__col-item etc. */
    .uwc-mm__v-panel .uwc-mm__col-header {
      padding-inline-start: ${t.space2};
    }
    .uwc-mm__v-panel .uwc-mm__col {
      flex: none;
      width: 100%;
    }

    /* ─────────────────────────────────────────────────────────────────────────
       RESPONSIVE
    ───────────────────────────────────────────────────────────────────────── */
    @media (max-width: 767px) {
      .uwc-mm__list      { display: none; }
      .uwc-mm__hamburger { display: inline-flex; }
      .uwc-mm__end { margin-inline-start: ${t.space2}; }
    }

    @media (min-width: 768px) {
      .uwc-mm__hamburger  { display: none !important; }
      .uwc-mm__mobile-panel { display: none !important; }
    }

    /* ─────────────────────────────────────────────────────────────────────────
       REDUCED MOTION
    ───────────────────────────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-mm__panel,
      .uwc-mm__mobile-panel {
        transition: none;
      }
      .uwc-mm__item-btn,
      .uwc-mm__v-item-btn,
      .uwc-mm__mob-btn,
      .uwc-mm__col-item,
      .uwc-mm__hamburger,
      .uwc-mm__chevron {
        transition: none;
      }
    }
  `,
];
